import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import ImpactCardExport from "@/components/ImpactCardExport";
import { CARD_SIZE, cardFilename, renderCardBlob, downloadBlob, canShareFiles, shareBlob } from "@/lib/cardImage";

const SHARE_TEXT = "Clear your next blocker at MinuteSwap — one problem, one move.";

// Renders the 1080x1080 export card off-screen and exposes Download PNG / native image share.
export default function CardExportActions({ data, publicUrl, className = "" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null);
  const filesSupported = canShareFiles();

  const render = async () => {
    const blob = await renderCardBlob(ref.current);
    if (!blob) throw new Error("render failed");
    return blob;
  };

  const download = async () => {
    setBusy("download");
    try {
      downloadBlob(await render(), cardFilename(data.display_name));
      toast.success("Impact Card downloaded.");
    } catch { toast.error("Couldn't generate the image. Please try again."); }
    finally { setBusy(null); }
  };

  const shareImage = async () => {
    setBusy("share");
    try {
      await shareBlob(await render(), cardFilename(data.display_name), { title: "My MinuteSwap Impact", text: SHARE_TEXT, url: publicUrl });
    } catch (err) {
      if (err?.name !== "AbortError") { toast("Image sharing isn't available here — downloading instead."); download(); return; }
    } finally { setBusy(null); }
  };

  return (
    <>
      <div className={`grid gap-2 ${filesSupported ? "grid-cols-2" : "grid-cols-1"} ${className}`}>
        <Button data-testid="download-png-btn" onClick={download} disabled={!!busy} className="btn-amber w-full">
          <Download className="mr-1 h-4 w-4" /> {busy === "download" ? "Rendering…" : "Download PNG"}
        </Button>
        {filesSupported && (
          <Button data-testid="share-image-btn" onClick={shareImage} disabled={!!busy} variant="outline" className="w-full border-white/15 text-white hover:bg-white/5">
            <Share2 className="mr-1 h-4 w-4" /> {busy === "share" ? "Preparing…" : "Share image"}
          </Button>
        )}
      </div>
      {createPortal(
        <div aria-hidden="true" style={{ position: "fixed", left: -20000, top: 0, width: CARD_SIZE, height: CARD_SIZE, pointerEvents: "none", zIndex: -1 }}>
          <ImpactCardExport data={data} forwardRef={ref} />
        </div>,
        document.body,
      )}
    </>
  );
}
